import { ComponentStyleConfig } from '@chakra-ui/react'
import { get } from 'lodash'
import { transparentize } from 'polished'
import { Link } from './Link'

const ROYAL_BLUE_COLOR = 'royalBlue'
const PRODUCT = 'product'
const GRAY_COLOR = 'gray'
const ORANGE_COLOR = 'orange'
const WHITE_COLOR = 'white'

export const VARIANTS = {
  SOLID: 'solid',
  OUTLINE: 'outline',
  GHOST: 'ghost',
  SOLIDALPHA10: 'solidAlpha10',
  PASSWORDINPUTTOGGLE: 'passwordInputToggle',
  ACCORDION: 'accordion',
  TAB: 'tab',
  TOGGLE: 'toggle',
  FAVORITE: 'favorite',
  PRODUCT_GLOBAL_NAV_LINK: 'product-global-nav-link',
  PRODUCT_LH_PANEL_LINK: 'product-lh-panel-link',
} as const

export const Button: ComponentStyleConfig = {
  baseStyle: { flexShrink: 0, _disabled: { opacity: 0.5 } },
  variants: {
    [VARIANTS.SOLID]: ({ colorScheme }) => {
      if (colorScheme === 'primary') {
        return {
          color: 'white',
          bg: 'product.primary',
          _hover: {
            bg: 'product.primaryHover',
          },
          _active: {
            bg: 'product.primaryActive',
          },
          _disabled: {
            bg: 'product.primaryDisabled',
            _hover: { bg: 'product.primaryDisabled' },
          },
        }
      }
      // FAI-10450 REVERT colorScheme things below once bcg/bain have own tenants
      if (colorScheme === 'bcg') {
        return {
          color: 'white',
          bg: '#04532D',
          _hover: {
            bg: '#034029',
          },
          _active: {
            bg: 'product.secondaryActive',
          },
          _disabled: {
            bg: 'product.secondaryDisabled',
            _hover: { bg: 'product.secondaryDisabled' },
          },
        }
      }
      if (colorScheme === 'bain') {
        return {
          color: 'white',
          bg: '#BF0404',
          _hover: {
            bg: '#D21E1E',
          },
          _active: {
            bg: 'product.secondaryActive',
          },
          _disabled: {
            bg: 'product.secondaryDisabled',
            _hover: { bg: 'product.secondaryDisabled' },
          },
        }
      }
      return {}
    },
    [VARIANTS.OUTLINE]: ({ colorScheme }: { colorScheme: string }) => {
      if (colorScheme === ROYAL_BLUE_COLOR) {
        return {
          _disabled: {
            _hover: {
              color: 'royalBlue.500',
            },
          },
          _hover: {
            backgroundColor: `${colorScheme}.100`,
          },
          color: `${colorScheme}.500`,
        }
      }
      if (colorScheme === PRODUCT) {
        return {
          color: 'product.primary',
          background: 'white',
          borderColor: 'product.border.solid',
          _hover: {
            backgroundColor: `product.cyan.50`,
          },
          _active: {
            bg: `product.blue.100`,
          },
        }
      }
      if (colorScheme === WHITE_COLOR) {
        return {
          color: 'white',
          background: 'product.primary',
          borderColor: 'white',
        }
      }
      return {
        color: `${colorScheme}.500`,
      }
    },
    [VARIANTS.GHOST]: ({ colorScheme }: { colorScheme: string }) => {
      const styles = { px: 4 }
      switch (colorScheme) {
        case ROYAL_BLUE_COLOR: {
          return {
            _hover: {
              backgroundColor: 'royalBlue.100',
            },
            color: `${colorScheme}.500`,
            ...styles,
          }
        }
        case GRAY_COLOR: {
          return {
            color: `${GRAY_COLOR}.525`,
            ...styles,
          }
        }
        case ORANGE_COLOR: {
          return {
            _hover: {
              backgroundColor: `${ORANGE_COLOR}.700`,
              color: 'white',
            },
            color: `${ORANGE_COLOR}.700`,
            ...styles,
          }
        }
        default: {
          return {
            color: `${colorScheme}.500`,
            ...styles,
          }
        }
      }
    },
    [VARIANTS.SOLIDALPHA10]: ({ colorScheme, theme }) => {
      const _disabled = {
        background: 'gray.100',
      }
      const color: string = get(theme, `colors.${colorScheme}.500`)
      return {
        color,
        _disabled,
        background: transparentize(0.9, color),
        _hover: {
          background: transparentize(0.85, color),
          _disabled,
        },
        _active: {
          background: transparentize(0.8, color),
        },
      }
    },
    [VARIANTS.PASSWORDINPUTTOGGLE]: {
      bg: 'transparent',
    },
    [VARIANTS.ACCORDION]: ({ colorScheme }) => {
      return {
        borderRadius: 'base',
        w: 'full',
        display: 'flex',
        justifyContent: 'space-between',
        _hover: { bg: `${colorScheme}.50` },
        px: 2,
        mb: 2,
      }
    },
    [VARIANTS.TAB]: {
      borderWidth: '1px',
      borderColor: 'product.border.solid',
      backgroundColor: 'white',
      color: 'product.text.dark.bodySubtle',
      '& svg': {
        color: 'product.primary',
      },
      _selected: {
        bg: 'product.cyan.50',
        borderColor: 'product.secondary',
        color: 'product.text.dark.bodySubtle',
      },
      _disabled: {
        opacity: 0.5,
        _hover: { borderColor: 'product.border.solid' },
      },
      _hover: {
        borderColor: 'product.secondary',
      },
    },
    [VARIANTS.TOGGLE]: {
      borderWidth: '1px',
      borderColor: 'product.border.subtle',
      backgroundColor: 'white',
      color: 'rgba(2, 61, 103, 0.4)', // product.primary
      _selected: {
        bg: 'product.cyan.50',
        color: 'product.primary',
      },
      _disabled: {
        opacity: 0.5,
        _hover: { borderColor: 'product.border.subtle' },
      },
      _hover: {
        borderColor: 'product.secondary',
      },
    },
    [VARIANTS.FAVORITE]: {
      color: 'product.primary',
      bg: 'white',
      borderColor: 'product.border.solid',
      borderWidth: '1px',
      _hover: {
        bg: 'product.cyan.50',
      },
      _active: {
        bg: 'product.blue.100',
      },
      _disabled: {
        opacity: 0.5,
      },
    },
    [VARIANTS.PRODUCT_GLOBAL_NAV_LINK]: {
      ...Link.variants['product-global-nav-link'],
      borderRadius: 'none',
      borderBottomColor: 'transparent',
      minH: 'full',
      h: 'full',
    },
    [VARIANTS.PRODUCT_LH_PANEL_LINK]: {
      ...Link.variants['product-lh-panel-link'],
      borderRadius: 'none',
    },
  },
  sizes: {
    xl: {
      fontSize: 'sm',
      h: 14,
      px: 6,
    },
    lg: {
      fontSize: 'product.md',
      h: 10,
      px: 4,
    },
    md: {
      fontSize: 'sm',
      h: 8,
      px: 4,
    },
    sm: { fontSize: 'xs', h: 8, px: 3 },
  },
}
